import React, { useState, useEffect, useRef } from 'react';
import { Palette, Type, Upload, Save, X, Loader2 } from 'lucide-react';
import Button from '../../components/Button';
import Input from '../../components/Input';
import ColorPicker from '../../components/ColorPicker';
import { useBrandSettings } from '../../hooks/useSupabase';
import { useAuth } from '../../contexts/AuthContext';
import { uploadBrandLogo, deleteBrandLogo } from '../../services/brandService';

const Brand: React.FC = () => {
  const { brandSettings, loading: loadingSettings, updateBrandSettings } = useBrandSettings();
  const { user, loading: authLoading } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [brandName, setBrandName] = useState('');
  const [tagline, setTagline] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#4F46E5');
  const [secondaryColor, setSecondaryColor] = useState('#10B981');
  const [accentColor, setAccentColor] = useState('#F59E0B');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Load existing brand settings
  useEffect(() => {
    if (brandSettings) {
      setBrandName(brandSettings.brand_name || '');
      setTagline(brandSettings.tagline || '');
      setPrimaryColor(brandSettings.primary_color);
      setSecondaryColor(brandSettings.secondary_color);
      setAccentColor(brandSettings.accent_color);
      setLogoUrl(brandSettings.logo_url);
      setPreviewUrl(brandSettings.logo_url);
    }
  }, [brandSettings]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setMessage({ type: 'error', text: 'Please upload PNG, JPG, or SVG files only.' });
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'File size must be less than 5MB.' });
      return;
    }

    setLogoFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setPreviewUrl(null);
    setLogoUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage(null);

      let finalLogoUrl = logoUrl;

      // Upload new logo if selected
      if (logoFile) {
        setUploadingLogo(true);
        const result = await uploadBrandLogo(logoFile);
        
        if (result.error) {
          throw new Error(result.error);
        }

        // Delete old logo if exists
        if (logoUrl && logoUrl !== result.url) {
          try {
            await deleteBrandLogo(logoUrl);
          } catch (err) {
            console.error('Failed to delete old logo:', err);
          }
        }

        finalLogoUrl = result.url;
        setUploadingLogo(false);
      }

      // If logo was removed and no new file selected
      if (!logoFile && !previewUrl && logoUrl) {
        try {
          await deleteBrandLogo(logoUrl);
        } catch (err) {
          console.error('Failed to delete logo:', err);
        }
        finalLogoUrl = null;
      }

      // Update brand settings
      await updateBrandSettings({
        brand_name: brandName || null,
        tagline: tagline || null,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        accent_color: accentColor,
        logo_url: finalLogoUrl
      });

      setLogoUrl(finalLogoUrl);
      setLogoFile(null);
      setMessage({ type: 'success', text: 'Brand settings saved successfully!' });
      
      // Log to console as per user preference
      console.log('Brand settings saved:', {
        brand_name: brandName,
        tagline,
        primary_color: primaryColor,
        secondary_color: secondaryColor,
        accent_color: accentColor,
        logo_url: finalLogoUrl
      });
    } catch (error: any) {
      console.error('Error saving brand settings:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save brand settings' });
    } finally {
      setSaving(false);
      setUploadingLogo(false);
    }
  };

  if (authLoading || loadingSettings) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-purple" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-h2 font-heading text-text-dark mb-2">
          Brand Settings
        </h1>
        <p className="text-body text-body-grey">
          Customize your brand identity for consistent mockups
        </p>
      </div>

      <div className="space-y-8">
        {/* Brand Information */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Type className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Brand Name
              </label>
              <Input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Enter your brand name"
              />
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Tagline
              </label>
              <Input
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Enter your brand tagline"
              />
            </div>
          </div>
        </div>

        {/* Brand Colors */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Palette className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Colors
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Primary Color
              </label>
              <ColorPicker
                color={primaryColor}
                onChange={setPrimaryColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: primaryColor }}></div>
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Secondary Color
              </label>
              <ColorPicker
                color={secondaryColor}
                onChange={setSecondaryColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: secondaryColor }}></div>
            </div>
            <div>
              <label className="block text-label font-body text-text-dark mb-2">
                Accent Color
              </label>
              <ColorPicker
                color={accentColor}
                onChange={setAccentColor}
                label=""
              />
              <div className="mt-2 h-20 rounded-lg" style={{ backgroundColor: accentColor }}></div>
            </div>
          </div>
        </div>

        {/* Brand Logo */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-text-dark mb-6 flex items-center">
            <Upload className="w-5 h-5 mr-2 text-brand-purple" />
            Brand Logo
          </h2>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/svg+xml"
            onChange={handleFileSelect}
            className="hidden"
          />
          
          {previewUrl ? (
            <div className="relative">
              <div className="border-2 border-gray-200 rounded-lg p-4 bg-gray-50">
                <img 
                  src={previewUrl} 
                  alt="Brand logo preview" 
                  className="max-h-32 mx-auto object-contain"
                />
              </div>
              <button
                onClick={handleRemoveLogo}
                className="absolute top-2 right-2 p-1 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
              >
                <X className="w-5 h-5 text-red-600" />
              </button>
              <p className="text-sm text-gray-600 mt-2 text-center">
                {logoFile ? 'New logo selected' : 'Current brand logo'}
              </p>
            </div>
          ) : (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-brand-purple transition-colors"
            >
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">Upload your brand logo</p>
              <p className="text-sm text-gray-500 mb-4">PNG, JPG or SVG. Max 5MB</p>
              <Button variant="secondary" size="small" onClick={() => {
                fileInputRef.current?.click();
              }}>
                Choose File
              </Button>
            </div>
          )}
        </div>

        {/* Message Display */}
        {message && (
          <div className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            variant="primary" 
            size="large"
            onClick={handleSave}
            disabled={saving || uploadingLogo}
          >
            {saving || uploadingLogo ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                {uploadingLogo ? 'Uploading Logo...' : 'Saving...'}
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Save Brand Settings
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Brand;
